(function () {
  const ALT_PRODUCT_LINK_SELECTOR = 'a[data-alt-product-link]';
  const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
  let pageFullyLoaded = document.readyState === 'complete';

  if (!pageFullyLoaded) {
    window.addEventListener(
      'load',
      () => {
        pageFullyLoaded = true;
      },
      { once: true }
    );

    document.addEventListener('click', handleEarlyAltProductNavigation, { capture: true });
  }
  // Remove the view transition render blocker if the user has reduced motion enabled or is on a low power device.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || isLowPowerDevice()) {
    viewTransitionRenderBlocker?.remove();
  } else {
    // If the browser didn't manage to parse the main content quickly, at least let the user see something.
    // We're aiming for the FCP to be under 1.8 seconds since the navigation started.
    const RENDER_BLOCKER_TIMEOUT_MS = Math.max(0, 1800 - performance.now());

    setTimeout(() => {
      viewTransitionRenderBlocker?.remove();
    }, RENDER_BLOCKER_TIMEOUT_MS);
  }

  const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

  window.addEventListener('pageswap', async (event) => {
    const { viewTransition } = /** @type {PageSwapEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    // Cancel view transition on user interaction to improve INP (Interaction to Next Paint)
    ['pointerdown', 'keydown'].forEach((eventName) => {
      document.addEventListener(
        eventName,
        () => {
          viewTransition.skipTransition();
        },
        { once: true }
      );
    });

    // Clean in case you landed on the pdp first. We want to remove the default transition type on the PDP media gallery so there is no duplicate transition name
    document
      .querySelectorAll('[data-view-transition-type]:not([data-view-transition-triggered])')
      .forEach((element) => {
        element.removeAttribute('data-view-transition-type');
      });

    const transitionTriggered = document.querySelector('[data-view-transition-triggered]');
    const transitionType = transitionTriggered?.getAttribute('data-view-transition-type');

    if (transitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(transitionType);
      sessionStorage.setItem('custom-transition-type', transitionType);
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
      sessionStorage.removeItem('custom-transition-type');
    }
  });

  window.addEventListener('pagereveal', async (event) => {
    const { viewTransition } = /** @type {PageRevealEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    const customTransitionType = sessionStorage.getItem('custom-transition-type');

    if (customTransitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(customTransitionType);

      await viewTransition.finished;

      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');

      idleCallback(() => {
        sessionStorage.removeItem('custom-transition-type');
        document.querySelectorAll('[data-view-transition-type]').forEach((element) => {
          element.removeAttribute('data-view-transition-type');
        });
      });
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
    }
  });

  /**
   * Force native navigation for alt product links until the page fully loads.
   * This avoids depending on delayed transition/hydration code for critical product color navigation.
   *
   * @param {MouseEvent} event
   */
  function handleEarlyAltProductNavigation(event) {
    if (pageFullyLoaded || event.defaultPrevented) return;

    const link = getAltProductLink(event);
    if (!link || !shouldUseEarlyNavigation(event, link)) return;

    event.preventDefault();
    event.stopPropagation();
    window.location.assign(link.href);
  }

  /**
   * @param {ViewTransition | null} viewTransition
   * @returns {viewTransition is null}
   */
  function shouldSkipViewTransition(viewTransition) {
    return !(viewTransition instanceof ViewTransition) || isLowPowerDevice();
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function isLowPowerDevice() {
    /* Skip ESLint compatibility check. Number(undefined) <= 2 is always false anyway. */
    /* eslint-disable-next-line compat/compat */
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }

  /**
   * @param {Event} event
   * @returns {HTMLAnchorElement | null}
   */
  function getAltProductLink(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];

    for (const node of path) {
      if (node instanceof HTMLAnchorElement && node.matches(ALT_PRODUCT_LINK_SELECTOR)) {
        return node;
      }

      if (!(node instanceof Element)) continue;

      const link = node.closest(ALT_PRODUCT_LINK_SELECTOR);
      if (link instanceof HTMLAnchorElement) {
        return link;
      }
    }

    return null;
  }

  /**
   * @param {MouseEvent} event
   * @param {HTMLAnchorElement} link
   * @returns {boolean}
   */
  function shouldUseEarlyNavigation(event, link) {
    if (!link.href || link.getAttribute('aria-current') === 'page' || link.hasAttribute('download')) {
      return false;
    }

    if (link.target && link.target.toLowerCase() !== '_self') {
      return false;
    }

    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  }
})();
